// React hooks
import { useCallback, useEffect, useState } from "react";

// Helpers i API servisi
import { useTranslate } from "helpers";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import {
  deleteFleetVehicleDamageAPI,
  getFleetVehicleDamagesAPI,
} from "services/api/fleet/vehicles/damages/api";
import { initialFleetVehicleDamageSearchForm } from "services/api/fleet/vehicles/damages/consts";
import {
  FleetVehicleDamageRow,
  FleetVehicleDamageSearchFormValues,
} from "services/api/fleet/vehicles/damages/types";

// Hook-ovi
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { Dialog } from "ui/components/Dialog";
import { NoResults } from "ui/components/NoResults";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { FLEET_DAMAGE_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

// Moduli specifični za projekt
import VehicleDamageEditCard from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleDamageEditCard/VehicleDamageEditCard";
import VehicleDamageSearchResults from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleDamageSearchPage/components/VehicleDamageSearchResults/VehicleDamageSearchResults";

// Lokalna navigacija
import { Routes } from "ui/modules/fleet/damages/navigation";

// Lokalne komponente
import DamageSearchFilters from "./components/DamageSearchFilters/DamageSearchFilters";
import { DamageSearchPageCmp, DamageSearchPageProps } from "./types";

/**
 * Štete kroz sva vozila flote. Isti popis kao na kartici vozila, samo bez filtra po
 * vozilu — pa svaki redak nosi i karticu vozila.
 */
const DamageSearchPage: DamageSearchPageCmp = (
  _props: DamageSearchPageProps
) => {
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleDamageRow>
  >(initialDataWithPagination);
  const [filterValues, setFilterValues] =
    useState<FleetVehicleDamageSearchFormValues>(
      initialFleetVehicleDamageSearchForm
    );
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [damageId, setDamageId] = useState<string>("");
  const [vehicleId, setVehicleId] = useState<string>("");
  const [showEditCard, setShowEditCard] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const fetchSearchResult = async (
    pagination: Pagination,
    values: FleetVehicleDamageSearchFormValues
  ) => {
    setIsLoading(true);
    try {
      // Bez `vehicleId` — endpoint tada vraća štete cijelog tenanta.
      setSearchResults(await getFleetVehicleDamagesAPI(pagination, values));
    } catch {
      setSearchResults(initialDataWithPagination);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchSearchResult(initialDataWithPagination.pagination, filterValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = useCallback(
    (values: FleetVehicleDamageSearchFormValues) => {
      setFilterValues(values);
      fetchSearchResult(searchResults.pagination, values);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchResults.pagination]
  );

  const handlePaginationChange = useCallback(
    (pagination: Pagination) => {
      fetchSearchResult(pagination, filterValues);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterValues]
  );

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  // Uređivanje traži i vozilo, a ono stiže u retku.
  const handleRowClick = useCallback(
    (id: string) => {
      const row = searchResults.data.find((claim) => claim.id === id);
      if (!row?.vehicle?.id) return;

      setVehicleId(row.vehicle.id);
      setDamageId(id);
      setShowEditCard(true);
    },
    [searchResults.data]
  );

  const handleEditClose = useCallback(() => {
    setShowEditCard(false);
    setDamageId("");
    setVehicleId("");
  }, []);

  const handleEditSuccess = useCallback(() => {
    handleEditClose();
    fetchSearchResult(searchResults.pagination, filterValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleEditClose, searchResults.pagination, filterValues]);

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>, id: string) => {
      event.stopPropagation();
      event.preventDefault();

      setDamageId(id);
      setOpenDialog(true);
    },
    []
  );

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!damageId) return;

    try {
      await deleteFleetVehicleDamageAPI(damageId, t);
      await fetchSearchResult(searchResults.pagination, filterValues);
    } catch (err) {
      console.error("Greška prilikom brisanja štete:", err);
    }

    setOpenDialog(false);
    setDamageId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [damageId, searchResults.pagination, filterValues, t]);

  const handleDeleteDialogClose = useCallback(() => {
    setDamageId("");
    setOpenDialog(false);
  }, []);

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <PageTitle
              title={FLEET_DAMAGE_SEARCH_PAGE_TITLE.text}
              icon={FLEET_DAMAGE_SEARCH_PAGE_TITLE.icon}
              iconColor={FLEET_DAMAGE_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
        </div>
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <DamageSearchFilters
            formValues={filterValues}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Nema šteta" />
          ) : (
            <VehicleDamageSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onDeleteClick={handleDeleteClick}
            />
          )}
        </div>
      </div>

      <VehicleDamageEditCard
        show={showEditCard}
        vehicleId={vehicleId}
        damageId={damageId}
        onSuccess={handleEditSuccess}
        onClose={handleEditClose}
      />

      <Dialog
        open={openDialog}
        title="Brisanje štete"
        message="Jeste li sigurni da želite obrisati štetu?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
};

DamageSearchPage.route = Routes.DamageSearchPageRouter;

export default DamageSearchPage;
